<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { NEMESIS_WEBHOOK_PATH } from 'dashboard/helper/nemesisEntryHelper';

// A dónde le pega el sistema que manda los casos, cómo se autentica y, una vez que se conectó bien,
// cómo llegó el body de su primer pedido (con los datos personales ocultos). No inventa ejemplos ni
// comandos de prueba: antes de la primera conexión no muestra ningún body, y después muestra el real.
// Es una foto del primer pedido, no se actualiza sola; "Volver a capturar" toma otra. Nunca muestra el
// token.
const props = defineProps({
  // La integración ya guardada (con sus `settings`). Sin ella (alta de una nueva) no hay estado.
  hook: { type: Object, default: null },
});
const emit = defineEmits(['reset']);

const { t } = useI18n();

const webhookUrl = `${window.location.origin}${NEMESIS_WEBHOOK_PATH}`;
const webhookLine = `POST ${webhookUrl}`;

const connectedAt = computed(() => props.hook?.settings?.connected_at || null);
const sampleBody = computed(() => props.hook?.settings?.sample_body || null);
const connectedAtLabel = computed(() =>
  connectedAt.value ? new Date(connectedAt.value).toLocaleString() : ''
);

const copyUrl = async () => {
  await copyTextToClipboard(webhookUrl);
  useAlert(t('INTEGRATION_APPS.NEMESIS.WEBHOOK.COPY_URL'));
};
</script>

<template>
  <div class="flex flex-col gap-2 mb-4">
    <div class="flex items-center gap-1.5">
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('INTEGRATION_APPS.NEMESIS.WEBHOOK.TITLE') }}
      </h4>
      <i
        v-tooltip.top="t('INTEGRATION_APPS.NEMESIS.WEBHOOK.TOOLTIP')"
        class="i-lucide-info text-n-slate-10 w-3.5 h-3.5 cursor-help"
      />
    </div>
    <section
      class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12"
    >
      <div class="ps-3 pe-1 py-2 flex justify-between items-center gap-2">
        <div class="flex flex-col min-w-0">
          <div class="text-n-slate-11 text-xs">
            {{ t('INTEGRATION_APPS.NEMESIS.WEBHOOK.URL_LABEL') }}
          </div>
          <div class="truncate">{{ webhookLine }}</div>
        </div>
        <NextButton
          type="button"
          ghost
          sm
          slate
          icon="i-lucide-copy"
          @click="copyUrl"
        />
      </div>
    </section>
    <p class="text-xs text-n-slate-11">
      {{ t('INTEGRATION_APPS.NEMESIS.WEBHOOK.AUTH_NOTE') }}
    </p>

    <section
      v-if="hook"
      data-test="connection"
      class="flex flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1 p-3"
    >
      <template v-if="!connectedAt">
        <div class="flex items-center gap-2 text-sm text-n-slate-12">
          <i class="i-lucide-loader-circle text-n-slate-10 w-4 h-4" />
          {{ t('INTEGRATION_APPS.NEMESIS.CONNECTION.WAITING_TITLE') }}
        </div>
        <p class="text-xs text-n-slate-11">
          {{ t('INTEGRATION_APPS.NEMESIS.CONNECTION.WAITING_HINT') }}
        </p>
      </template>
      <template v-else>
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-2 text-sm text-n-teal-11">
            <i class="i-lucide-circle-check w-4 h-4" />
            {{
              t('INTEGRATION_APPS.NEMESIS.CONNECTION.CONNECTED_TITLE', {
                date: connectedAtLabel,
              })
            }}
          </div>
          <NextButton
            type="button"
            ghost
            sm
            slate
            icon="i-lucide-refresh-cw"
            :label="t('INTEGRATION_APPS.NEMESIS.CONNECTION.RECAPTURE')"
            @click="emit('reset')"
          />
        </div>
        <template v-if="sampleBody">
          <h5 class="text-sm font-medium text-n-slate-12">
            {{ t('INTEGRATION_APPS.NEMESIS.CONNECTION.SAMPLE_TITLE') }}
          </h5>
          <pre
            data-test="sample-body"
            class="rounded-xl border border-n-weak bg-n-alpha-black2 w-full text-xs text-n-slate-12 p-3 overflow-x-auto"
          ><code>{{ sampleBody }}</code></pre>
          <p class="text-xs text-n-slate-11">
            {{ t('INTEGRATION_APPS.NEMESIS.CONNECTION.SAMPLE_NOTE') }}
          </p>
        </template>
        <p v-else class="text-xs text-n-slate-11">
          {{ t('INTEGRATION_APPS.NEMESIS.CONNECTION.NO_BODY_YET') }}
        </p>
      </template>
    </section>
  </div>
</template>
